<template>
    <nav class="dashboard-nav bg-black container">
      <div class=" nav-container">
        <div class="nav-brand" @click="goHome">
          <img src="../assets/qb-logos/quickbeam_logo_full.svg" class="logo-img" />
        </div>

        <div class="" ref="menuRoot" v-if="!user">
          <router-link to="/login" class="btn btn-text">Login</router-link>
          <router-link to="/register" class="btn btn-primary">Register</router-link>
        </div>
    
        <div class="nav-user" ref="menuRoot" v-if="user">
          <!-- Status Indicator -->
          <div class="hide-mobile">
            <StatusIndicator/>
          </div>

          <span class="user-info hide-mobile">
            <span class="user-name">{{ user.displayName || user.email }}</span>
          </span>

          <!-- Hamburger / Close button -->
          <button
              class="hamburger"
              :aria-expanded="isOpen.toString()"
              aria-haspopup="menu"
              :aria-label="isOpen ? 'Close menu' : 'Open menu'"
              @click="toggleMenu"
              @keydown.escape.prevent="closeMenu"
          >
            <span class="line line-top" :class="{ open: isOpen }"></span>
            <span class="line line-bottom" :class="{ open: isOpen }"></span>
          </button>

          <!-- Dropdown -->
          <div
            class="menu"
            role="menu"
            v-show="isOpen"
          >
            <router-link to="/settings" role="menuitem" class="menu-item" @click="closeMenu">
              Settings
            </router-link>
            <router-link to="/projects" role="menuitem" class="menu-item" @click="closeMenu">
              Projects
            </router-link>
            <router-link to="/api-keys" role="menuitem" class="menu-item" @click="closeMenu">
              API Keys
            </router-link>
            <router-link to="/api-endpoints" role="menuitem" class="menu-item" @click="closeMenu">
              API Endpoints
            </router-link>
            <router-link to="/api-docs" role="menuitem" class="menu-item" @click="closeMenu">
              API Docs
            </router-link>
            <router-link to="/connections" role="menuitem" class="menu-item" @click="closeMenu">
              Connections
            </router-link>
            <button
              role="menuitem"
              class="menu-item menu-danger"
              @click="onLogoutFromMenu"
              data-testid="logout-button"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </nav>
  </template>
  
  <script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useAuthStore } from '../stores/auth'
import { logout } from '../services/auth'
import { useRouter } from 'vue-router'
import StatusIndicator from './StatusIndicator.vue'

const authStore = useAuthStore()
const router = useRouter()

// Make user reactive - this will update when auth state changes
// Use firebaseUser OR user (whichever is available)
const user = computed(() => authStore.user || authStore.firebaseUser)
const isAuthenticated = computed(() => authStore.isAuthenticated || !!authStore.firebaseUser)

function goHome() {
  router.push('/dashboard')
}

async function handleLogout() {
  try {
    await logout()
    authStore.clearAuth()
    router.push('/')
  } catch (error) {
    console.error('Logout failed:', error)
  }
}

// Dropdown state
const isOpen = ref(false)
const menuRoot = ref<HTMLElement | null>(null)

function toggleMenu() {
  isOpen.value = !isOpen.value
}
function closeMenu() {
  isOpen.value = false
}
async function onLogoutFromMenu() {
  closeMenu()
  await handleLogout()
}

// Close on outside click
function onDocClick(e: MouseEvent) {
  if (!isOpen.value) return
  const root = menuRoot.value
  if (root && !root.contains(e.target as Node)) {
    closeMenu()
  }
}
onMounted(() => document.addEventListener('click', onDocClick))
onBeforeUnmount(() => document.removeEventListener('click', onDocClick))
</script>
  
<style scoped>
  .dashboard-nav {
    border-bottom: 1px solid #191919;
  }

  .nav-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0px;
  }

  .nav-brand {
    display: inline-flex;
    flex-direction: row;
    align-items: center;
  }

  .nav-brand p {
    color: #ffffff;
    font-size: 24px;
    font-family: 'Lucida Sans', 'Lucida Sans Regular', 'Lucida Grande', 'Lucida Sans Unicode', Geneva, Verdana, sans-serif;
  }

  .user-name {
    color: #ffffff;
    margin-right: 1rem;
  }

  .logo-img {
    height: 50px;
    width: auto;
    overflow: visible;
    fill: none;
    transition: transform 0.3s ease, filter 0.3s ease;
  }

  .logo-img:hover {
    cursor: pointer;
    filter: drop-shadow(0 0 10px rgba(0, 245, 255, 0.8));
    transform: scale(1.01);
  }

  /* Container aligns right side content */
  .nav-user {
    position: relative;            /* anchor for dropdown */
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  
  /* Two-line hamburger that morphs into an X */
  .hamburger {
    position: relative;
    width: 36px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #0a0a0a;
    border-radius: 8px;
    padding: 0;
    cursor: pointer;
    transition: box-shadow 0.25s ease, transform 0.2s ease;
  }
  .hamburger:hover {
    border: none;
    transition: none;
    transform: none;
  }
  
  /* Lines */
  .line {
    position: absolute;
    width: 18px;
    height: 2px;
    background: #e6e6e6;
    transition: transform 0.25s ease, opacity 0.2s ease, top 0.25s ease, background 0.25s ease;
    will-change: transform, opacity, top;
  }
  
  /* Two lines only */
  .line-top   { top: 9px; }
  .line-bottom{ top: 17px; }
  
  /* Morph to X */
  .line-top.open {
    top: 13px;
    transform: rotate(45deg);
    background: #00e7f0; /* slight cyan hint when open */
  }
  .line-bottom.open {
    top: 13px;
    transform: rotate(-45deg);
    background: #00bfae; /* slight teal hint when open */
  }
  
  /* Dropdown menu */
  .menu {
    position: absolute;
    right: 0;
    top: calc(100% + 10px);
    min-width: 220px;
    background: #0c0c0c;
    border: 1px solid #1e1e1e;
    border-radius: 10px;
    padding: 8px;
    box-shadow:
      0 8px 24px rgba(0,0,0,0.35),
      0 0 28px rgba(0, 245, 255, 0.08);
    z-index: 20;
    animation: fadeInScale 180ms ease-out;
  }
  
  /* Menu items */
  .menu-item {
    display: block;
    width: 100%;
    text-align: left;
    padding: 10px 12px;
    border-radius: 8px;
    color: #e8e8e8;
    text-decoration: none;
    font-size: 0.95rem;
    line-height: 1.2;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
  }
  .menu-item:hover {
    background: #1f1f1f;
    color: #ffffff;
  }
  .menu-danger {
    color: #ff5b6e;
  }
  .menu-danger:hover {
    background: rgba(255, 91, 110, 0.12);
    color: #ff95a2;
  }
  
  /* Tiny show animation */
  @keyframes fadeInScale {
    from { opacity: 0; transform: translateY(-6px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0)    scale(1); }
  }

  .btn-primary {
    padding: 0.5rem 1rem;
  }

  .btn-primary:hover {
    transform: scale(1.01);
    transition: transform 0.3s ease, filter 0.3s ease;

  }

  .btn-text {
    display: inline-block;
  padding: 0.5rem 1.3rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  }

  .btn-text:hover {
    transform: scale(1.01);
    transition: transform 0.3s ease-in-out, filter 0.3s ease;
    background-color: #262626;

  }

</style>